import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import PaperCard from '@/components/papers/PaperCard';
import { Paper } from '@/types/paper';

interface CommunitySectionProps {
  featuredPaper: Paper | null;
  papers: Paper[];
  isLoading: boolean;
}

const PaperSkeleton = ({ featured = false }: { featured?: boolean }) => (
  <div className={cnSkeleton(featured)}>
    <Skeleton className="h-4 w-28" />
    <Skeleton className="mt-4 h-7 w-full" />
    <Skeleton className="mt-2 h-7 w-3/4" />
    <Skeleton className="mt-5 h-20 w-full" />
  </div>
);

const cnSkeleton = (featured: boolean) =>
  `border border-border/50 bg-card p-6 shadow-sm ${featured ? 'min-h-72' : 'min-h-64'}`;

const CommunitySection: React.FC<CommunitySectionProps> = ({ featuredPaper, papers, isLoading }) => (
  <section className="px-5 py-16 md:px-8 md:py-24" aria-labelledby="community-title">
    <div className="mx-auto max-w-6xl">
      <div className="mb-10 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between md:mb-14">
        <div className="max-w-2xl">
          <p className="mb-3 text-xs font-semibold uppercase text-primary">Public research on PaperVerse</p>
          <h2 id="community-title" className="text-3xl font-medium md:text-4xl">Research you can inspect, review, and discuss</h2>
          <p className="mt-4 text-base leading-7 text-muted-foreground">Private drafts stay private. These authors chose to make their work public.</p>
        </div>
        <Button asChild variant="outline">
          <Link to="/explore" data-track="community_explore_all" data-track-category="navigation">
            Explore all research <ArrowRight aria-hidden="true" />
          </Link>
        </Button>
      </div>

      <div className="mb-5 flex items-center gap-2 text-sm font-medium text-foreground">
        <Star className="h-4 w-4 text-primary" aria-hidden="true" />
        Editor's Spotlight
      </div>
      {isLoading ? <PaperSkeleton featured /> : featuredPaper ? <PaperCard paper={featuredPaper} featured compactFeatured /> : (
        <div className="border border-dashed border-border bg-card/50 px-6 py-12 text-center text-sm text-muted-foreground">No featured public paper is available yet.</div>
      )}

      <div className="mt-12 mb-5 text-sm font-medium text-muted-foreground">Recently published</div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {isLoading
          ? [0, 1, 2].map((key) => <PaperSkeleton key={key} />)
          : papers.map((paper) => <PaperCard key={paper.id} paper={paper} />)}
      </div>
      {!isLoading && papers.length === 0 && (
        <p className="border-y border-border/60 py-8 text-center text-sm text-muted-foreground">No recent public papers are available yet.</p>
      )}
    </div>
  </section>
);

export default CommunitySection;